import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HERO_IMG = 'https://public.readdy.ai/ai/img_res/sandbox/ec5ef1d3c4675c57d9407dc9f601fa86/e42fb93a-155e-4a10-986e-6d55b764f5c6_auren-hero.webp';

const CENTER_Y = 50; // visual center of composition, in %

const startRadius = (mobile: boolean) => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return mobile ? Math.min(w * 0.36, h * 0.22) : Math.min(h * 0.29, w * 0.19);
};
const coverRadius = () => Math.hypot(window.innerWidth, window.innerHeight) + 60;

export default function Hero() {
  const root = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return undefined;
    const q = gsap.utils.selector(el);
    const clip = el.querySelector<HTMLElement>('.hero-clip');
    const glow = el.querySelector<HTMLElement>('.hero-glow');
    const mm = gsap.matchMedia();

    // Feathered circular mask driven by a single proxy state
    const p = { x: 50, y: CENTER_Y, r: 0, f: 14 };
    const render = () => {
      if (!clip) return;
      const inner = Math.max(p.r - p.f, 0);
      const mask = `radial-gradient(circle at ${p.x}% ${p.y}%, #000 ${inner}px, transparent ${p.r}px)`;
      clip.style.webkitMaskImage = mask;
      clip.style.maskImage = mask;
      if (glow) {
        const size = p.r * 2.6;
        glow.style.width = `${size}px`;
        glow.style.height = `${size}px`;
        glow.style.left = `${p.x}%`;
        glow.style.top = `${p.y}%`;
      }
    };

    // Fit headlines into the space outside the circle (no overlap, 40px gap)
    const heads = Array.from(el.querySelectorAll<HTMLElement>('.hero-head'));
    const layoutHeads = () => {
      if (heads.length < 2) return;
      const w = window.innerWidth;
      const h = el.clientHeight || window.innerHeight;
      const mobile = w <= 768;
      const r = startRadius(mobile);
      const cy = (h * CENTER_Y) / 100;
      const gap = 32;
      heads.forEach((hd) => { hd.style.fontSize = '100px'; });
      const maxW = Math.max(...heads.map((hd) => hd.scrollWidth)) || 1;
      const [left, right] = heads;
      if (!mobile) {
        const pad = w >= 1024 ? 48 : 32;
        const avail = w / 2 - r - gap - pad;
        const size = Math.max(Math.min((100 * avail) / maxW, 96), 14);
        // Put the visual gap between line 1 and line 2 exactly on the circle's center
        const cs = getComputedStyle(left);
        const ctx2d = document.createElement('canvas').getContext('2d');
        const L = 0.95 * size;
        let top = cy - L; // fallback: line break at cy
        if (ctx2d) {
          ctx2d.font = `${cs.fontWeight} ${size}px ${cs.fontFamily}`;
          const m = ctx2d.measureText('CLARITY');
          const A = m.fontBoundingBoxAscent || size * 0.8;
          const D = m.fontBoundingBoxDescent || size * 0.2;
          const capH = m.actualBoundingBoxAscent || size * 0.7;
          const baseline = (L - (A + D)) / 2 + A;
          top = cy - baseline - (L - capH) / 2;
        }
        heads.forEach((hd) => {
          hd.style.fontSize = `${size}px`;
          hd.style.top = `${top}px`;
          hd.style.bottom = 'auto';
          hd.style.transform = 'none';
        });
        left.style.left = `${pad}px`; left.style.right = 'auto';
        right.style.right = `${pad}px`; right.style.left = 'auto';
      } else {
        const pad = 16;
        const above = cy - r - 32 - 76; // room below navbar
        const below = h - (cy + r + 32) - 72; // room above bottom labels
        const size = Math.max(Math.min((100 * (w - pad * 2)) / maxW, 44, above / 1.95, below / 1.95), 14);
        heads.forEach((hd) => { hd.style.fontSize = `${size}px`; hd.style.transform = 'none'; });
        left.style.left = `${pad}px`; left.style.right = 'auto';
        left.style.top = 'auto'; left.style.bottom = `${h - (cy - r - 32)}px`;
        right.style.right = `${pad}px`; right.style.left = 'auto';
        right.style.bottom = 'auto'; right.style.top = `${cy + r + 32}px`;
      }
    };
    layoutHeads();

    const setStatic = () => {
      p.x = 50; p.y = CENTER_Y; p.r = startRadius(window.innerWidth <= 768); p.f = 14;
      render();
    };
    setStatic();

    mm.add({
      desktop: '(min-width: 769px) and (prefers-reduced-motion: no-preference)',
      mobile: '(max-width: 768px) and (prefers-reduced-motion: no-preference)',
    }, (ctx) => {
      const isMobile = Boolean(ctx.conditions?.mobile);
      const dx = isMobile ? 17 : 21; // % offsets — moderate flashlight sweep
      const dy = isMobile ? 7 : 10;
      gsap.set(q('.hero-img'), { scale: 1.12 });
      gsap.set(q('.hero-final'), { autoAlpha: 0, y: 16 });
      gsap.set(q('.hero-tint'), { opacity: 0 });
      gsap.set(q('.hero-head'), { autoAlpha: 1 });

      const tl = gsap.timeline({
        defaults: { ease: 'sine.inOut' },
        onUpdate: render,
        scrollTrigger: {
          trigger: el,
          start: 'top top',
          end: isMobile ? '+=264%' : '+=454%',
          scrub: 1.2,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onRefresh: render,
        },
      });

      tl.fromTo(p, { x: 50, y: CENTER_Y, r: () => startRadius(isMobile), f: 14 }, {
        x: 50 - dx, y: CENTER_Y - dy * 0.6, duration: 0.3,
      }, 0)
        // Stage 1 — flashlight-like swings (longer scroll span)
        .to(p, { x: 50 + dx, y: CENTER_Y - dy, duration: 0.33 }, 0.3)
        .to(p, { x: 50 - dx * 0.85, y: CENTER_Y + dy, duration: 0.33 }, 0.63)
        .to(p, { x: 50 + dx * 0.7, y: CENTER_Y + dy * 0.5, duration: 0.24 }, 0.96)
        // Stage 2 — return to center
        .to(p, { x: 50, y: CENTER_Y, duration: 0.1 }, 1.2)
        // Stage 3 — expand to fullscreen
        .to(p, {
          r: () => coverRadius(), f: 0, duration: 0.36, ease: 'power2.in',
        }, 1.3)
        // Headlines leave before the circle travels toward them
        .to(q('.hero-head'), { autoAlpha: 0, duration: 0.1, ease: 'none' }, 0)
        .to(q('.hero-img'), { scale: 1.0, duration: 1.66, ease: 'none' }, 0)
        .to(q('.hero-glow'), { opacity: 0, duration: 0.15 }, 1.35)
        .to(q('.hero-fade'), { autoAlpha: 0, y: -24, stagger: 0.02, duration: 0.2 }, 1.32)
        .to(q('.hero-tint'), { opacity: 1, duration: 0.15, ease: 'none' }, 1.54)
        .to(q('.hero-final'), { autoAlpha: 1, y: 0, duration: 0.1, ease: 'none' }, 1.66)
        .to({}, { duration: 0.04 });

      return () => { gsap.set(q('.hero-head'), { clearProps: 'opacity,visibility' }); setStatic(); };
    });

    const onResize = () => { layoutHeads(); if (!ScrollTrigger.getAll().length) setStatic(); };
    window.addEventListener('resize', onResize);
    const img = el.querySelector('img');
    const refresh = () => ScrollTrigger.refresh();
    img?.addEventListener('load', refresh);
    document.fonts?.ready.then(() => { layoutHeads(); refresh(); });

    return () => {
      window.removeEventListener('resize', onResize);
      img?.removeEventListener('load', refresh);
      mm.revert();
    };
  }, []);

  const headline = 'hero-head absolute m-0 whitespace-nowrap font-heading font-light uppercase text-background-50 leading-[0.95] tracking-[-0.01em] text-[40px] mix-blend-difference';

  return (
    <section id="top" ref={root} className="relative h-screen min-h-[560px] w-full overflow-hidden bg-secondary-950">
      <div className="hero-glow absolute -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none bg-[radial-gradient(circle,oklch(var(--accent-500)/0.14)_0%,oklch(var(--accent-500)/0.05)_38%,transparent_62%)]" />

      <div className="hero-clip absolute inset-0">
        <img src={HERO_IMG} alt="Modern glass skyscraper facade in cool blue tones — AUREN independent advisory" title="AUREN management consulting perspective" fetchPriority="high" decoding="async" className="hero-img absolute inset-0 w-full h-full object-cover object-center will-change-transform" />
        <div className="hero-tint absolute inset-0 bg-primary-500/45 opacity-0" />
      </div>

      {/* Headlines balanced around the circle */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <h1 className={`${headline} text-left`}>
          Clarity.<br />In Action.
        </h1>
        <p className={`${headline} text-right`} aria-hidden="false">
          Perspective.<br />In Depth.
        </p>
      </div>

      <div className="relative z-10 h-full w-full px-4 md:px-16 lg:px-20 pt-24 md:pt-28 pb-8 flex flex-col justify-between pointer-events-none">
        <div className="flex justify-end">
          <span className="hero-fade label-xs text-background-50/60 hidden md:block">Independent Advisory</span>
        </div>
        <div className="flex items-end justify-between gap-4">
          <span className="hero-fade label-xs text-background-50/60">Strategy / Transformation</span>
          <span className="hero-fade label-xs text-background-50/60 flex items-center gap-3">
            Scroll to explore
            <span className="w-8 h-8 rounded-full border border-background-50/30 flex items-center justify-center"><i className="ri-arrow-down-line" /></span>
          </span>
        </div>
      </div>

      <div className="hero-final invisible absolute inset-0 z-20 flex items-center justify-center px-4 pointer-events-none">
        <p className="font-heading font-light uppercase text-background-50 text-center tracking-[0.12em] text-xl sm:text-3xl lg:text-5xl">Perspective changes everything.</p>
      </div>
    </section>
  );
}